import { isPlatform, useIonLoading } from '@ionic/react';
import { Location } from 'history';
import { isEmpty } from 'lodash';
import { useCallback, useRef } from 'react';
import { useHistory, useLocation } from 'react-router';
import {
  DirectConnectGetSyncDataResponse,
  DirectConnectSyncDataInput,
  DisableMachineInput,
  Machine,
  ProvisionedMachineClaim,
  useGetDirectConnectSyncDataLazyQuery,
} from '../../api/graphql/generated';
import { WifiInfo } from '../../api/hooks/wifi.hooks';
import { ApplicationConfig, isLocalDev } from '../../config/config';
import i18n from '../../i18n';
import { useBeaconRouter } from '../../route/route.hooks';
import { logger } from '../../util/logger';
import { removeProperties } from '../../util/object';
import { useDirectConnectNetworkContext } from '../api/direct-connect-network.provider';
import { useAddDirectConnectCachedMachine } from '../cache/direct-connect-machine-cache';
import { useDirectConnectAddTask } from '../cache/direct-connect-task-cache';
import {
  DirectConnectMachine,
  DirectConnectTask,
  DirectConnectTaskType,
} from '../model/direct-connect.model';

const isDirectConnectSupportedForPlatform = () => {
  return isPlatform('ios') || isLocalDev();
};

const getDirectConnectClientUrl = (ipAddr: string): string => {
  const port = ApplicationConfig.robot.api.port;
  const path = ApplicationConfig.robot.api.path;

  // If no host specified, use current page's origin
  if (isEmpty(ipAddr)) {
    const url = new URL(path, window.location.origin);
    return url.toString();
  }

  const url = new URL(path, `http://${ipAddr}:${port}`);
  return url.toString();
};

const getBeaconWifiSsid = (serialNumber: string): string => {
  return `Beacon-${serialNumber}`;
};

const getBeaconWifiSerialNumber = (wifiInfo?: WifiInfo): string | undefined => {
  if (!wifiInfo?.ssid) {
    logger.info('Wifi info does not have ssid', { wifiInfo });
    return undefined;
  }

  const matches = wifiInfo.ssid.match(beaconWifiSsidPattern);
  if (!matches) {
    logger.info(
      'Wifi info ssid does not match beacon ssid pattern `Beacon-<serial-number>`',
      {
        ssid: wifiInfo.ssid,
      }
    );
    return undefined;
  }

  return matches[1];
};

const createDirectConnectProvisionTaskFromClaim = (
  serialNumber: string,
  claim: ProvisionedMachineClaim & { clientId: string }
): DirectConnectTask => {
  return {
    taskType: DirectConnectTaskType.PROVISION,
    serialNumber,
    payload: {
      clientId: claim.clientId,
      certificatePem: claim.certificatePem,
      privateKey: claim.privateKey,
    },
  };
};

const createDirectConnectSynchronizeTaskFromSyncResponse = (
  serialNumber: string,
  response: DirectConnectGetSyncDataResponse
): DirectConnectTask => {
  const modified = removeProperties(response, ['__typename']);

  return {
    taskType: DirectConnectTaskType.SYNCHRONIZE,
    serialNumber,
    payload: modified as DirectConnectSyncDataInput,
  };
};

const createDirectConnectDeactivateTask = (
  serialNumber: string,
  request: DisableMachineInput
): DirectConnectTask => {
  return {
    taskType: DirectConnectTaskType.DEACTIVATE,
    serialNumber,
    payload: request,
  };
};

const createDirectConnectMachineFromCloudMachine = (
  machine: Machine
): DirectConnectMachine => {
  return {
    id: machine.id,
    name: machine.name,
    serialNumber: machine.state?.runtimeInfo.serialNumber,
    teamId: machine.teamId,
  };
};

const useNavigateToMachineHelper = () => {
  const { builder, performWindowNavigation } = useBeaconRouter();

  const addDirectConnectMachineToCache = useAddDirectConnectCachedMachine();

  const callback = useCallback(
    async (machine: DirectConnectMachine) => {
      await addDirectConnectMachineToCache(machine);

      const route = builder.getDirectConnectMachineRoute(machine.id);

      logger.info('navigating to direct connect machine', { machine, route });
      performWindowNavigation(route);
    },
    [builder, performWindowNavigation, addDirectConnectMachineToCache]
  );

  return callback;
};

const useDirectConnectGoBack = () => {
  const { builder } = useBeaconRouter();
  const { goBack, replace } = useHistory();
  const location = useLocation();

  const locationRef = useRef<Location<unknown>>(location);
  locationRef.current = location;

  const networkContext = useDirectConnectNetworkContext();

  const callback = useCallback(async () => {
    if (networkContext) {
      await networkContext.disconnectNetwork({
        disableGateError: true,
      });
    }

    if (!locationRef.current?.key) {
      goBack();
    } else {
      replace(builder.getDirectConnectSelectMachineRoute());
    }
  }, [networkContext, goBack, replace, builder]);

  return callback;
};

const useDirectConnectSaveTasksAndNavigateToMachine = () => {
  const addTask = useDirectConnectAddTask();
  const navigateToDirectConnectMachine = useNavigateToMachineHelper();

  const callback = useCallback(
    async (machine: DirectConnectMachine, tasks: DirectConnectTask[]) => {
      const taskTypes = tasks.map((task) => task.taskType);

      logger.info('saving direct connect tasks and navigating to machine', {
        machine,
        taskTypes,
      });

      for (const task of tasks) {
        await addTask(task);
      }

      navigateToDirectConnectMachine(machine);
    },
    [addTask, navigateToDirectConnectMachine]
  );

  return callback;
};

const useDirectConnectNavigateToMachine = () => {
  const saveTasksAndNavigateToMachine =
    useDirectConnectSaveTasksAndNavigateToMachine();

  const [getDataToSync] = useGetDirectConnectSyncDataLazyQuery();
  const [present, dismiss] = useIonLoading();

  const isSynchronizingRef = useRef(false);

  const synchronizeDirectConnectMachine = useCallback(
    async (machine: Machine) => {
      const { id: machineId, teamId } = machine;
      const serialNumber = machine.state?.runtimeInfo?.serialNumber;

      if (!serialNumber || isSynchronizingRef.current) {
        return;
      }

      isSynchronizingRef.current = true;
      present(i18n.t('common:direct_connect_synchronizing'));

      const dataToSyncResponse = await getDataToSync({
        variables: {
          input: {
            machineId,
            teamId,
          },
        },
        fetchPolicy: 'no-cache',
      });

      const dataToSync = dataToSyncResponse.data?.getDirectConnectSyncData;

      if (!dataToSync) {
        logger.error('no server data to sync to direct connect machine', {
          machineId,
          serialNumber,
        });
        return;
      }

      const synchronizeTask =
        createDirectConnectSynchronizeTaskFromSyncResponse(
          serialNumber,
          dataToSync
        );

      const directConnectMachine =
        createDirectConnectMachineFromCloudMachine(machine);

      dismiss();
      isSynchronizingRef.current = false;

      await saveTasksAndNavigateToMachine(directConnectMachine, [
        synchronizeTask,
      ]);
    },
    [present, getDataToSync, dismiss, saveTasksAndNavigateToMachine]
  );

  return synchronizeDirectConnectMachine;
};

const beaconWifiSsidPattern = /^Beacon-(\d+)$/;

export {
  isDirectConnectSupportedForPlatform,
  useDirectConnectSaveTasksAndNavigateToMachine,
  createDirectConnectProvisionTaskFromClaim,
  createDirectConnectSynchronizeTaskFromSyncResponse,
  createDirectConnectMachineFromCloudMachine,
  getBeaconWifiSsid,
  getBeaconWifiSerialNumber,
  getDirectConnectClientUrl,
  createDirectConnectDeactivateTask,
  useDirectConnectGoBack,
  useDirectConnectNavigateToMachine,
};
